/**
 * ServiceDetailModal — service detail overlay
 *
 * Shows:
 *   - Service title, description, tags
 *   - Plan list with metric-expressive pricing (SL-R-009)
 *   - Subscribe CTA (delegates to existing SubscribeModal for the chosen plan)
 *
 * Data source: GET /api/broker/v1/services/:slug/plans (usePlans hook)
 *
 * Design contract:
 *   - Consistent with existing SubscribeModal overlay pattern (var CSS, dp-btn)
 *   - Preserves empty/loading/error states per existing conventions
 *   - en-only strings (i18n beyond en is flag+defer per brief)
 *
 * Note: description_detail field is not yet in the DB schema (pending V2/M5).
 *   Using service.description as the detail content; evidence records this gap.
 *
 * WP-SL-V3 / DOPLA-686
 */

import { useEffect, type MouseEvent } from 'react';
import type { TileConfig } from '../config/schema';
import { useAuth } from '../auth/KeycloakProvider';
import { usePlans, type Plan } from '../hooks/usePlans';
import PricingLabel from './PricingLabel';
import UatDecisionPanel from './UatDecisionPanel';
import SetPublicPanel from './SetPublicPanel';
import { useConfigState } from '../config/ConfigContext';
import { t } from '../i18n';
import type { CartItem } from '../pages/CartPage';

interface ServiceDetailModalProps {
  tile: TileConfig;
  onClose: () => void;
  onOrder: () => void;
  onAddToCart?: (item: CartItem) => void;
  /**
   * brief-20 / convention-admin-catalogue-no-commerce-cta: in the admin
   * catalogue the modal shows plans and pricing as information only — no
   * order button, no add-to-cart. Suppressing the tile pill alone would just
   * relocate the button here.
   */
  isAdminCatalog?: boolean;
}

function PlanCard({ plan, onAddToCart }: { plan: Plan; onAddToCart?: () => void }) {
  const currencies = Object.keys(plan.pricing);
  const currency = currencies[0] ?? 'CHF';
  const pricing = plan.pricing[currency];
  const isTrial = pricing && pricing.price_cents === 0 && pricing.trial_days > 0;

  return (
    <div
      style={{
        borderWidth: '1px',
        borderStyle: 'solid',
        borderColor: plan.is_default ? 'var(--dp-teal-300)' : 'var(--border-default)',
        borderRadius: 'var(--radius-md)',
        padding: 'var(--sp-4)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--sp-2)',
        background: plan.is_default ? 'var(--dp-teal-050)' : 'var(--bg-recessed)',
        position: 'relative',
      }}
    >
      {plan.is_default && (
        <span
          style={{
            position: 'absolute',
            top: 'var(--sp-3)',
            right: 'var(--sp-3)',
            fontSize: 'var(--fs-eyebrow)',
            fontWeight: 600,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            padding: '2px 8px',
            borderRadius: 'var(--radius-sm)',
            background: 'var(--dp-teal-100)',
            color: 'var(--dp-teal-700)',
          }}
        >
          {t('serviceDetail.plan.default')}
        </span>
      )}
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 'var(--sp-2)', flexWrap: 'wrap' }}>
        <span style={{
          fontFamily: 'var(--font-serif)',
          fontSize: 'var(--fs-h4)',
          fontWeight: 600,
          color: 'var(--fg-1)',
        }}>
          {plan.name}
        </span>
        {isTrial && pricing && (
          <PricingLabel currency={currency} pricing={pricing} variant="tile" />
        )}
      </div>
      {plan.description && (
        <p style={{
          fontFamily: 'var(--font-sans)',
          fontSize: 'var(--fs-body-sm)',
          color: 'var(--fg-3)',
          margin: 0,
          lineHeight: 'var(--lh-body-sm)',
        }}>
          {plan.description}
        </p>
      )}
      {pricing && !isTrial && (
        <PricingLabel currency={currency} pricing={pricing} variant="detail" />
      )}
      {onAddToCart && (
        <button
          className="dp-btn dp-btn--sm dp-btn--ghost"
          onClick={onAddToCart}
          style={{ alignSelf: 'flex-start', marginTop: 'var(--sp-1)' }}
        >
          + Add to cart
        </button>
      )}
    </div>
  );
}

export default function ServiceDetailModal({
  tile, onClose, onOrder, onAddToCart, isAdminCatalog = false,
}: ServiceDetailModalProps) {
  const commerce = !isAdminCatalog;
  const { token, user } = useAuth();
  const plansState = usePlans(tile.id, token);
  const { config } = useConfigState();
  const setPublicGate = config?.features?.setPublicGate === true;
  const isTenantAdmin = (user?.roles ?? []).includes('tenant-admin');

  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  function handleBackdropClick(e: MouseEvent<HTMLDivElement>) {
    if (e.target === e.currentTarget) onClose();
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="service-detail-title"
      onClick={handleBackdropClick}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 500,
        background: 'var(--scrim)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 'var(--sp-8)',
      }}
    >
      <div
        style={{
          background: 'var(--bg-surface)',
          borderRadius: 'var(--radius-lg)',
          borderWidth: '1px',
          borderStyle: 'solid',
          borderColor: 'var(--border-default)',
          width: '100%',
          maxWidth: '560px',
          maxHeight: '80vh',
          overflow: 'auto',
          padding: 'var(--sp-8)',
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--sp-6)',
          boxShadow: 'var(--shadow-2)',
        }}
      >
        {/* Header */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-3)' }}>
              <div
                style={{
                  width: '44px',
                  height: '44px',
                  borderRadius: 'var(--radius-md)',
                  background: 'var(--dp-navy-800)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <span style={{
                  fontFamily: 'var(--font-serif)',
                  fontWeight: 600,
                  fontSize: '18px',
                  color: 'var(--fg-on-navy)',
                  lineHeight: 1,
                }}>
                  {tile.mark}
                </span>
              </div>
              <h2
                id="service-detail-title"
                style={{
                  fontFamily: 'var(--font-serif)',
                  fontSize: 'var(--fs-h3)',
                  fontWeight: 600,
                  color: 'var(--fg-1)',
                  lineHeight: 'var(--lh-h3)',
                  margin: 0,
                }}
              >
                {tile.title}
              </h2>
            </div>
            <button
              onClick={onClose}
              aria-label={t('serviceDetail.close')}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: 'var(--sp-1)',
                color: 'var(--fg-3)',
                fontSize: '20px',
                lineHeight: 1,
                flexShrink: 0,
              }}
            >
              ✕
            </button>
          </div>

          {tile.description && (
            <p style={{
              fontFamily: 'var(--font-sans)',
              fontSize: 'var(--fs-body)',
              color: 'var(--fg-2)',
              lineHeight: 'var(--lh-body)',
              margin: 0,
            }}>
              {tile.description}
            </p>
          )}

          {tile.tags.length > 0 && (
            <div style={{ display: 'flex', gap: 'var(--sp-1)', flexWrap: 'wrap' }}>
              {tile.tags.map((tag) => (
                <span
                  key={tag}
                  style={{
                    fontFamily: 'var(--font-sans)',
                    fontSize: 'var(--fs-eyebrow)',
                    fontWeight: 600,
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                    padding: '2px 8px',
                    borderRadius: 'var(--radius-sm)',
                    background: 'var(--dp-navy-050)',
                    color: 'var(--fg-2)',
                  }}
                >
                  {tag}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* App UAT gate (ADR-075 steps 3–4) — decision surface for the active deployment */}
        {tile.deployment?.phase === 'uat' && (
          <UatDecisionPanel
            tile={tile}
            onDecided={() => setTimeout(() => window.location.reload(), 1800)}
          />
        )}

        {/* App business gate (WP-ACP-3) — visible per the SET_PUBLIC_GATE flag */}
        <SetPublicPanel
          tile={{ id: tile.id, title: tile.title, status: tile.status }}
          token={token}
          setPublicGate={setPublicGate}
          isTenantAdmin={isTenantAdmin}
          onDone={() => setTimeout(() => window.location.reload(), 1800)}
        />

        {/* Plans */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)' }}>
          <p className="dp-eyebrow">{t('serviceDetail.plansEyebrow')}</p>

          {(plansState.tag === 'idle' || plansState.tag === 'loading') && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-2)' }}>
              {[1, 2].map((i) => (
                <div
                  key={i}
                  style={{
                    height: '72px',
                    borderRadius: 'var(--radius-md)',
                    background: 'var(--bg-recessed)',
                    animation: 'dp-pulse 1.4s ease-in-out infinite',
                  }}
                />
              ))}
            </div>
          )}

          {plansState.tag === 'loaded' && plansState.plans.length === 0 && (
            <p style={{
              fontFamily: 'var(--font-sans)',
              fontSize: 'var(--fs-body-sm)',
              color: 'var(--fg-3)',
              margin: 0,
            }}>
              {t('serviceDetail.noPlans')}
            </p>
          )}

          {plansState.tag === 'loaded' && plansState.plans.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)' }}>
              {plansState.plans.map((plan) => {
                const currencies = Object.keys(plan.pricing);
                const currency = currencies[0] ?? 'CHF';
                const pricing = plan.pricing[currency];
                return (
                  <PlanCard
                    key={plan.id}
                    plan={plan}
                    onAddToCart={onAddToCart && commerce ? () => onAddToCart({
                      service_id: tile.id,
                      service_name: tile.title,
                      plan_id: plan.id,
                      plan_name: plan.name,
                      price_cents: pricing?.price_cents ?? 0,
                      currency,
                      trial_days: pricing?.trial_days ?? 0,
                    }) : undefined}
                  />
                );
              })}
            </div>
          )}

          {plansState.tag === 'error' && (
            <p style={{
              fontFamily: 'var(--font-sans)',
              fontSize: 'var(--fs-body-sm)',
              color: 'var(--dp-crimson-600)',
              margin: 0,
            }}>
              {t('serviceDetail.plansError')}
            </p>
          )}
        </div>

        {/* Actions */}
        <div style={{
          display: 'flex',
          gap: 'var(--sp-3)',
          justifyContent: 'flex-end',
          paddingTop: 'var(--sp-2)',
          borderTop: '1px solid var(--border-subtle)',
        }}>
          <button className="dp-btn dp-btn--ghost" onClick={onClose}>
            {t('destructive.cancel')}
          </button>
          {tile.action === 'order' && commerce && (
            <button
              className="dp-btn dp-btn--primary"
              onClick={onOrder}
            >
              {t('tile.cta.order')}
            </button>
          )}
          {tile.action === 'launch' && (
            <button className="dp-btn dp-btn--primary" onClick={onClose}>
              {t('tile.cta.launch')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
